import { createSelector } from '@reduxjs/toolkit'
import { createStructuredSelector } from 'reselect'

import { buildDataForBomDestroy } from '../utils/builders';
import { extractBom } from '../utils/extracters';

import { selectAssemblySelectedQuantity } from 'product_tabs/tab_assembly/selector/selector';

import { PCB_PARTS_ID } from '../data/BomConstants';

//Selectors to select specific data from the parts state
const selectBomId = (state) => state.bom?.id;
const selectBomName = (state) => state.bom?.name;
const selectBomRevision = (state) => state.bom?.revision;
const selectBomVersion = (state) => state.bom?.version;
const selectBomBoardQtys = (state) => state.bom?.board_qtys;
const selectBomSelectedBoardQty = (state) => state.bom?.selected_board_qty;
const selectBomFile = (state) => state.bom?.bom_file;
const selectBomStatus = (state) => state.bom?.bom_status;
const selectBomLineItems = (state) => state.bom?.bom_line_items;
const selectBomTotals = (state) => state.bom?.totals;
const selectBomMarkups = (state) => state.bom?.markups;
const selectBomShipping = (state) => state.bom?.shipping;
const selectBomTariffs = (state) => state.bom?.tariffs;
const selectBomBaseTotals = (state) => state.bom?.base_totals;
const selectBomQuoteType = (state) => state.bom?.quote_type;
const selectBomPreferences = (state) => state.bom?.preferences;
const selectBomLineItemId = (state) => state.bom?.line_item_id;
const selectBomProductQuoteNumber = (state) => state.bom?.product_quote_number;
const selectBomCurrentVariant = (state) => state.bom?.currentVariant;
const selectBomSaved = (state) => state.bom?.saved;
const selectBomImportParameters = (state) => state.bom?.data_for_import.bom_parameters;
const selectBomImportWorkbook = (state) => state.bom?.data_for_import.workbook;
const selectBomImportHeader = (state) => state.bom?.data_for_import.header;
const selectBomImportFirstRow = (state) => state.bom?.data_for_import.first_row;
const selectBomImportRefDesSeparator = (state) => state.bom?.data_for_import.ref_des_separator;
const selectBomImportRefDesRangeSeparator = (state) => state.bom?.data_for_import.ref_des_range_separator;
const selectBomQuoteProgressStep = (state) => state.bom?.quote_progress.step;
const selectBomQuoteProgressTotalSteps = (state) => state.bom?.quote_progress.total_steps;
const selectBomQuoteProgressCurrentPn = (state) => state.bom?.quote_progress.current_pn;
const selectBomViewSubstitutes = (state) => state.bom?.view.substitutes;
const selectBomViewChangedZipFile = (state) => state.bom?.view.changed_zip_file;
const selectBomViewDestroy = (state) => state.bom?.view.destroy;
const selectBomViewLoading = (state) => state.bom?.view.loading;
const selectBomViewMessages = (state) => state.bom?.view.messages;
const selectBomViewShowModal = (state) => state.bom?.view.show_modal;
const selectBomViewZipFileInputId = (state) => state.bom?.view.zip_file_input_id;
const selectBomViewUploadedZipFile = (state) => state.bom?.view.uploaded_zip_file;
const selectBomViewAvailableBomInZip = (state) => state.bom?.view.available_bom_in_zip;
const selectDirectory = (state) => state.bom?.view.directory;
const selectShowBomSelection = (state) => state.bom?.view.show_bom_selection;
const selectBomSelected = (state) => state.bom?.view.bom_selected;
const selectFabricationSlug = (state) => state.bom?.view.fabrication_slug;

const selectChinaFabricationQuantities = (state) => state.fabrication?.parameters?.quantities;
const selectTaiwanFabricationQuantities = (state) => state.fabrication?.parameters?.quantities;
const selectUSAFabricationQuantities = (state) => state.fabrication?.parametersCustomer?.quantities;
const selectAssemblyFabricationQuantities = (state) => state.assembly?.parameters?.quantities;

const selectActiveTab = (state) => state.tab?.activeTab;

const selectBomInitialSelectedBoardQty = createSelector([
    selectBomBoardQtys,
    selectAssemblySelectedQuantity,
  ], (
    board_qtys,
    assembly_board_quantity,
  ) => {
    const default_board_qty = board_qtys && board_qtys.length ? board_qtys[0] : null
    const converted_assembly_board_quantity = assembly_board_quantity ? parseInt(assembly_board_quantity) : null
    return converted_assembly_board_quantity || default_board_qty
  }
)


//Selectors to be used in the components to select needed data from the parts state
export const selectDataForTabView = createStructuredSelector({
  id: selectBomId,
  bom_parameters: selectBomImportParameters,
  bom_line_items: selectBomLineItems,
  bom_status: selectBomStatus,
  selected_board_qty: selectBomSelectedBoardQty,
  saved: selectBomSaved,
  substitutes: selectBomViewSubstitutes,
  loading: selectBomViewLoading,
  messages: selectBomViewMessages,
  show_modal: selectBomViewShowModal,
  show_bom_selection: selectShowBomSelection,
  uploaded_zip_file: selectBomViewUploadedZipFile,
  bom_selected: selectBomSelected,
  available_bom_in_zip: selectBomViewAvailableBomInZip,
  step: selectBomQuoteProgressStep,
  total_steps: selectBomQuoteProgressTotalSteps,
  markups: selectBomMarkups,
});

export const selectDataForZipProcessFileForParts = createStructuredSelector({
  bom_saved: selectBomSaved,
})

export const selectDataForBomSelection = createStructuredSelector({
  directory: selectDirectory,
  show: selectShowBomSelection,
  activeTab: selectActiveTab,
})

export const selectDataForBomReadFile = createStructuredSelector({
  zip_file_input_id: selectBomViewZipFileInputId,
  activeTab: selectActiveTab,
})

export const selectDataForBomTabChange = createStructuredSelector({
  available_bom_in_zip: selectBomViewAvailableBomInZip,
  bom_saved: selectBomSaved,
  bom_selected: selectBomSelected,
  changed_zip_file: selectBomViewChangedZipFile,
  uploaded_zip_file: selectBomViewUploadedZipFile,
})

export const selectDataForReview = createStructuredSelector({
  bom_line_items: selectBomLineItems,
  saved: selectBomSaved,
  selected_board_qty: selectBomSelectedBoardQty,
  status: selectBomStatus,
  markups: selectBomMarkups,
  bom_status: selectBomStatus,
});

export const selectDataForQuoteView = createStructuredSelector({
  quote_step: selectBomQuoteProgressStep,
  quote_total_steps: selectBomQuoteProgressTotalSteps,
  board_qtys: selectBomBoardQtys,
  base_totals: selectBomBaseTotals,
  markups: selectBomMarkups,
  shipping: selectBomShipping,
  tariffs: selectBomTariffs,
  totals: selectBomTotals,
  initial_selected_board_qty: selectBomInitialSelectedBoardQty,
});

export const selectDataForImportView = createStructuredSelector({
  name: selectBomName,
  revision: selectBomRevision,
  board_qtys: selectBomBoardQtys,
  header: selectBomImportHeader,
  first_row: selectBomImportFirstRow,
  ref_des_separator: selectBomImportRefDesSeparator,
  ref_des_range_separator: selectBomImportRefDesRangeSeparator,
  workbook: selectBomImportWorkbook,
});

export const selectDataForQuoteToApi = createStructuredSelector({
  first_row: selectBomImportFirstRow,
  name: selectBomName,
  board_qtys: selectBomBoardQtys,
  quote_type: selectBomQuoteType,
  revision: selectBomRevision,
  bom_parameters: selectBomImportParameters,
  workbook: selectBomImportWorkbook,
  initial_selected_board_qty: selectBomInitialSelectedBoardQty,
});

export const selectDataForBomSave = createStructuredSelector({
  id: selectBomId,
  name: selectBomName,
  revision: selectBomRevision,
  board_qtys: selectBomBoardQtys,
  selected_board_qty: selectBomSelectedBoardQty,
  totals: selectBomTotals,
  base_totals: selectBomBaseTotals,
  markups: selectBomMarkups,
  shipping: selectBomShipping,
  tariffs: selectBomTariffs,
  bom_line_items: selectBomLineItems,
  currentVariant: selectBomCurrentVariant,
});

export const selectDataForBomValidation = createStructuredSelector({
  bomSaved: selectBomSaved,
  bomChangedZipFile: selectBomViewChangedZipFile,
  bomCurrentVariant: selectBomCurrentVariant,
});

export const selectDataForPartSearch = createStructuredSelector({
  board_qtys: selectBomBoardQtys,
})

export const selectDataToInitializeBom = createSelector([
    selectFabricationSlug,
    selectChinaFabricationQuantities,
    selectTaiwanFabricationQuantities,
    selectUSAFabricationQuantities,
    selectAssemblyFabricationQuantities,
  ], (
    fabrication_slug,
    fabrication_china_quantities, 
    fabrication_taiwan_quantities, 
    fabrication_usa_quantities, 
    assembly_quantities,
  ) => {
    const fabrication_quantities_by_slug = {
      'china_pcb': fabrication_china_quantities,
      'taiwan_pcb': fabrication_taiwan_quantities,
      'usa_quickturn_pcb': fabrication_usa_quantities,
    }
    const fabrication_quantities = fabrication_quantities_by_slug[fabrication_slug] ? fabrication_quantities_by_slug[fabrication_slug] : []

    const quantities = assembly_quantities && assembly_quantities.length ? assembly_quantities : fabrication_quantities
    const board_qtys = quantities ? quantities.map(qty => parseInt(qty)) : [null]

    return {
      board_qtys,
      fabrication_slug,
    }
  }
)

export const selectBomDataForCart = createSelector([
    selectBomFile,
    selectBomName, 
    selectBomProductQuoteNumber,
    selectBomRevision,
    selectBomSaved,
    selectBomCurrentVariant,
    selectBomSelectedBoardQty,
    selectBomTotals,
    selectBomLineItemId,
    selectBomViewDestroy,
    selectBomViewZipFileInputId, 
  ], (
    bom_file, 
    name, 
    product_quote_number, 
    revision, 
    saved,
    currentVariant,
    selected_board_qty,
    totals,
    line_item_id,
    destroy,
    zip_file_input_id,
  ) => {

    const bom_tab_activated = currentVariant === PCB_PARTS_ID;

    const data_for_form = bom_tab_activated ? {
      bom: {
        bom_file: bom_file, 
        bomPartNumber: name,
        bomProductQuoteNumber: product_quote_number, 
        bomRevision: revision, 
        bomSaved: saved,
        bomVariant: currentVariant,
        mainProductInputFileId: zip_file_input_id,
      },
    } : {};

    const extract_args = bom_tab_activated ? { saved, selected_board_qty, totals } : null;
    const destroy_args = bom_tab_activated ? { line_item_id, destroy } : null;

    return {
      partDataForForms: data_for_form,
      partProps: extractBom(extract_args),
      partToDestroy: buildDataForBomDestroy(destroy_args),
    }
  }
);